import React, { useState, useEffect } from 'react';
import moment from 'moment';
import { NavLink } from 'react-router-dom';
import { withRouter } from 'react-router';
import { useIsUser, useIsManager, useIsAdmin, useStore } from 'hooks';
import { Select, Range, DateRange } from 'components/ui/form';
import { getActiveFiltersCount } from '../active-filters';
import styles from './filter.module.scss';

const Filter = ({
  filters,
  query = {},
  setQuery,
  archiveMode = 'active',
  onArchiveModeChange,
  showArchiveModeToggle = false,
  match: {
    params: { userId },
  },
  hideMenu = false,
  showFilter = false,
  onResetFilters,
}) => {
  const { socket } = useStore();
  const [emit, setEmit] = useState(false);
  const [supply, setSupply] = useState(0);
  const [wagon, setWagon] = useState(0);
  const [popupOpen, setPopupOpen] = useState(false);
  const isUser = useIsUser();
  const isAdmin = useIsAdmin();
  const isManager = useIsManager();
  const isAdminOrManager = isAdmin || isManager;
  const isProfile = isAdminOrManager && Number(userId) >= 0;

  const profileSupply = isProfile ? `/profile/${userId}/applications/supply` : '/applications/supply';
  const toSupply = isUser ? '/profile/applications/supply' : profileSupply;

  const profileWagon = isProfile ? `/profile/${userId}/applications/wagon` : '/applications/wagon';
  const toWagon = isUser ? '/profile/applications/wagon' : profileWagon;

  useEffect(() => {
    if (isAdminOrManager && socket) {
      socket.on('APPLICATION_CHECK_COUNT', (data) => {
        if (userId && data.userId && Number(data.userId) === Number(userId)) {
          setWagon(data.wagon);
          setSupply(data.supply);
        } else if (!userId && !data.userId) {
          setWagon(data.wagon);
          setSupply(data.supply);
        }
      });

      if (!emit) {
        socket.emit('APPLICATION_CHECK_COUNT', userId);
        setEmit(true);
      }
    }

    return () => {
      if (isAdminOrManager && socket) {
        socket.off('APPLICATION_CHECK_COUNT');
      }
    };
  }, [emit, socket, supply, wagon, userId, isAdminOrManager]);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setPopupOpen(false);
    };
    if (popupOpen) {
      document.addEventListener('keydown', onKeyDown);
      return () => document.removeEventListener('keydown', onKeyDown);
    }
  }, [popupOpen]);

  const hasFilters = showFilter && Object.keys(filters).length > 0;
  const hasFilterControls = hasFilters || (showArchiveModeToggle && typeof onArchiveModeChange === 'function');
  const activeCount = hasFilters ? getActiveFiltersCount(filters, query) : 0;

  const scrollMainToTop = () => {
    const main = document.querySelector('main');
    if (main) main.scrollTop = 0;
  };

  const filterFormContent = hasFilters ? (
    <>
      <div className={styles.formSection}>
        <div className={styles.formHeader}>
          <h3 className={styles.formTitle}>Фильтры</h3>
          <button
            type="button"
            className={styles.formClose}
            onClick={() => setPopupOpen(false)}
            aria-label="Закрыть"
          >
            ×
          </button>
        </div>
        <div className={styles.list}>
        {Object.keys(filters)
          .map(key => ({ key, ...filters[key] }))
          .filter(({ values, allowEmpty }) => (Array.isArray(values) && values.length) || allowEmpty)
          .map(({
            key,
            min,
            max,
            type,
            field,
            label,
            values,
            alias,
            defaultValue,
            emptyValue = true,
          }) => (
            <div key={key} className={styles.input}>
              {type === 'select' ? (
                <Select
                  label={label}
                  onChange={({ target: { value } }) => setQuery({ [alias || field]: value })}
                  defaultValue={query[alias || field] !== undefined ? query[alias || field] : defaultValue}
                >
                  {emptyValue ? (
                    <option value="">Выбрать</option>
                  ) : null}
                  {values.map(({ id, value }) => (
                    <option key={id} value={id}>{value}</option>
                  ))}
                </Select>
              ) : null}
              {type === 'range' ? (
                <Range
                  label={label}
                  min={min}
                  max={max}
                  initialMin={query[`${alias || field}Min`]}
                  initialMax={query[`${alias || field}Max`]}
                  onChangeComplete={value => setQuery({
                    [`${alias || field}Min`]: value.min,
                    [`${alias || field}Max`]: value.max,
                  })}
                  allowSameValues
                />
              ) : null}
              {type === 'date-range' ? (
                <DateRange
                  labelStart="Дата (Начало)"
                  labelEnd="Дата (Конец)"
                  min={min}
                  max={max}
                  valueStart={query.dateMin ? new Date(Number(query.dateMin)) : undefined}
                  valueEnd={query.dateMax ? new Date(Number(query.dateMax)) : undefined}
                  onChange={({ startDate, endDate }) => setQuery({
                    [`${alias || field}Min`]: moment(startDate).toDate().getTime(),
                    [`${alias || field}Max`]: moment(endDate).toDate().getTime(),
                  })}
                />
              ) : null}
            </div>
          ))}
        </div>
        {typeof onResetFilters === 'function' && activeCount > 0 ? (
          <button
            type="button"
            className={styles.resetBtn}
            onClick={() => {
              onResetFilters();
              setPopupOpen(false);
            }}
          >
            Сбросить фильтр
          </button>
        ) : null}
      </div>
    </>
  ) : null;

  const hasMenu = !hideMenu;
  if (!hasMenu && !hasFilterControls) {
    return null;
  }

  return (
    <section className={styles.filter}>
      <div className={styles.row}>
        {hasMenu ? (
          <div className={styles.menu}>
            <NavLink
              exact
              className={styles.item}
              activeClassName={styles.active}
              to={toSupply}
              onClick={scrollMainToTop}
            >
              <span className={styles.flex}>
                Заявки на поставку
                {isAdminOrManager && Boolean(supply) && (
                <>
                  {' '}
                  <span className={styles.notification}>{supply}</span>
                </>
                )}
              </span>
            </NavLink>
            <NavLink
              exact
              className={styles.item}
              activeClassName={styles.active}
              to={toWagon}
              onClick={scrollMainToTop}
            >
              <span className={styles.flex}>
                Заявки на вагоны
                {isAdminOrManager && Boolean(wagon) && (
                  <>
                    {' '}
                    <span className={styles.notification}>{wagon}</span>
                  </>
                )}
              </span>
            </NavLink>
          </div>
        ) : null}
        {hasFilterControls ? (
          <div className={styles.triggerWrap}>
            {showArchiveModeToggle && typeof onArchiveModeChange === 'function' ? (
              <div className={styles.modeToggle}>
                <button
                  type="button"
                  className={archiveMode === 'active' ? `${styles.modeBtn} ${styles.modeBtnActive}` : styles.modeBtn}
                  onClick={() => onArchiveModeChange('active')}
                >
                  Активные
                </button>
                <button
                  type="button"
                  className={archiveMode === 'archive' ? `${styles.modeBtn} ${styles.modeBtnActive}` : styles.modeBtn}
                  onClick={() => onArchiveModeChange('archive')}
                >
                  Архивные
                </button>
              </div>
            ) : null}
            {hasFilters ? (
              <button
                type="button"
                className={activeCount > 0 ? `${styles.trigger} ${styles.hasBadge}` : styles.trigger}
                onClick={() => setPopupOpen(true)}
              >
                Фильтры
                {activeCount > 0 && (
                  <span className={styles.triggerBadge}>{activeCount}</span>
                )}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      {popupOpen && hasFilters ? (
        <>
          <div
            className={styles.popupBackdrop}
            role="presentation"
            onClick={() => setPopupOpen(false)}
          />
          <div className={styles.popup} role="dialog" aria-label="Фильтры">
            <div className={styles.form}>
              {filterFormContent}
            </div>
          </div>
        </>
      ) : null}
    </section>
  );
};

export default withRouter(Filter);
